import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, RefreshCw, Send, Plus, Globe, X, Tag } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { uploadImage } from "../../lib/uploads";

const SECTIONS = [
  { id: "vintage_luxury", en: "Vintage & Luxury", ar: "ساعات فاخرة وكلاسيكية" },
  { id: "sport_chronograph", en: "Sport & Chronograph", ar: "رياضية وكرونوغراف" },
  { id: "new_arrivals", en: "New Arrivals", ar: "جديد" },
  { id: "on_sale", en: "On Sale", ar: "تخفيضات" },
];

const T = {
  en: {
    title: "Snap & Submit", subtitle: "Take a photo with your device camera, fill the basics, and submit for admin approval.",
    open_camera: "Open Camera", capture: "Capture", recapture: "Re-capture", upload_instead: "Upload from Gallery",
    name: "Product Name *", brand: "Brand", category: "Category", price: "Price (AED) *", original_price: "Original Price (optional)",
    stock: "Stock", description: "Description", sections: "Storefront Sections (multi-select)",
    is_new: "Tag as New (auto-shown in New Arrivals)", new_category: "+ New Category", submit: "Submit for Approval",
    new_cat_label: "Add a new category", new_cat_name: "Name (English) *", new_cat_ar: "Name (Arabic)", new_cat_save: "Add Category",
    photo_required: "Please capture or upload at least one photo", price_required: "Price must be greater than 0",
    submitted: "Submitted for admin approval", lang_en: "EN", lang_ar: "AR",
  },
  ar: {
    title: "التقاط وإرسال", subtitle: "التقط صورة بكاميرا جهازك، أدخل التفاصيل، وأرسل للمراجعة من المسؤول.",
    open_camera: "فتح الكاميرا", capture: "التقاط", recapture: "إعادة التقاط", upload_instead: "رفع من المعرض",
    name: "اسم المنتج *", brand: "العلامة التجارية", category: "الفئة", price: "السعر (درهم) *", original_price: "السعر الأصلي (اختياري)",
    stock: "المخزون", description: "الوصف", sections: "أقسام المتجر (متعدد)",
    is_new: "وسم جديد (يظهر في الوافدين الجدد)", new_category: "+ فئة جديدة", submit: "إرسال للمراجعة",
    new_cat_label: "إضافة فئة جديدة", new_cat_name: "الاسم (إنجليزي) *", new_cat_ar: "الاسم (عربي)", new_cat_save: "أضف فئة",
    photo_required: "يرجى التقاط أو رفع صورة واحدة على الأقل", price_required: "يجب أن يكون السعر أكبر من 0",
    submitted: "تم الإرسال للمراجعة من المسؤول", lang_en: "EN", lang_ar: "AR",
  },
};

export default function ShopCameraForm() {
  const [lang, setLang] = useState("en");
  const t = T[lang];
  const dir = lang === "ar" ? "rtl" : "ltr";

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [images, setImages] = useState([]);

  const [brands, setBrands] = useState([]);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    name: "", brand: "", category: "", price: "", original_price: "",
    stock: 1, description: "", sections: [], is_new: false,
  });
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCat, setNewCat] = useState({ name: "", name_ar: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/brands").then((r) => { setBrands(r.data); if (r.data?.[0] && !form.brand) setForm((f) => ({ ...f, brand: r.data[0].id })); });
    refreshCats();
    return () => stopCamera();
    // eslint-disable-next-line
  }, []);

  const refreshCats = () => {
    api.get("/categories").then((r) => {
      setCats(r.data);
      if (r.data?.[0] && !form.category) setForm((f) => ({ ...f, category: r.data[0].id }));
    });
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraOn(true);
    } catch (e) {
      toast.error("Camera permission denied or not available. Please use Upload from Gallery instead.");
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraOn(false);
  };

  const capture = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current; const c = canvasRef.current;
    c.width = v.videoWidth || 1024; c.height = v.videoHeight || 1024;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    c.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" });
      try {
        const r = await uploadImage(file, "products/camera");
        setImages((s) => [...s, r.url]);
        toast.success(r.storage === "s3" ? "Captured & uploaded to S3" : "Captured");
      } catch (e) { toast.error(formatErr(e)); }
    }, "image/jpeg", 0.92);
  };

  const handleGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (images.length + files.length > 5) return toast.error("Max 5 images");
    for (const f of files) {
      if (f.size > 4 * 1024 * 1024) { toast.error(`${f.name} > 4 MB — skipped`); continue; }
      try { const r = await uploadImage(f, "products/camera"); setImages((s) => [...s, r.url]); } catch (err) { toast.error(formatErr(err)); }
    }
  };

  const removeImage = (i) => setImages((s) => s.filter((_, idx) => idx !== i));

  const toggleSection = (id) => setForm((s) => ({
    ...s,
    sections: s.sections.includes(id) ? s.sections.filter((x) => x !== id) : [...s.sections, id],
  }));

  const addCategory = async () => {
    if (!newCat.name) return toast.error("Name is required");
    try {
      const r = await api.post("/categories", newCat);
      toast.success("Category added");
      setShowNewCat(false); setNewCat({ name: "", name_ar: "" });
      await refreshCats();
      setForm((f) => ({ ...f, category: r.data.id }));
    } catch (e) { toast.error(formatErr(e)); }
  };

  const submit = async () => {
    if (images.length < 1) return toast.error(t.photo_required);
    if (!form.price || parseFloat(form.price) <= 0) return toast.error(t.price_required);
    setSaving(true);
    try {
      await api.post("/products", {
        ...form,
        price: parseFloat(form.price),
        original_price: form.original_price ? parseFloat(form.original_price) : null,
        stock: parseInt(form.stock) || 0,
        images,
      });
      toast.success(t.submitted);
      setForm({ name: "", brand: brands[0]?.id || "", category: cats[0]?.id || "", price: "", original_price: "", stock: 1, description: "", sections: [], is_new: false });
      setImages([]);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" dir={dir}>
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div>
          <div className="overline mb-2">{lang === "ar" ? "كاميرا" : "Camera"}</div>
          <h1 className="font-display text-4xl text-white">{t.title}</h1>
          <p className="text-sm text-white/55 mt-2 max-w-2xl">{t.subtitle}</p>
        </div>
        {/* Language toggle */}
        <div className="flex gap-1 border border-white/15 p-1">
          {[
            { id: "en", label: t.lang_en },
            { id: "ar", label: t.lang_ar },
          ].map((l) => {
            const sel = lang === l.id;
            return (
              <button
                key={l.id}
                data-testid={`cam-lang-${l.id}`}
                onClick={() => setLang(l.id)}
                className="px-3 py-1.5 text-[11px] uppercase tracking-luxe transition-colors flex items-center gap-1"
                style={sel ? { backgroundColor: "#B89766", color: "#fff" } : { color: "rgba(255,255,255,0.7)" }}
              >
                <Globe size={11}/> {l.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Camera area */}
        <div className="card-luxe p-5">
          <div className="overline mb-3">1. {lang === "ar" ? "الصورة" : "Photo"}</div>
          <div className="relative aspect-video bg-[#070707] border border-white/10 overflow-hidden flex items-center justify-center">
            {cameraOn ? (
              <video ref={videoRef} className="w-full h-full object-cover" autoPlay playsInline muted/>
            ) : (
              <div className="text-center text-white/40 text-xs uppercase tracking-luxe">{lang === "ar" ? "الكاميرا غير نشطة" : "Camera Off"}</div>
            )}
            <canvas ref={canvasRef} className="hidden"/>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {!cameraOn ? (
              <button data-testid="cam-open" onClick={startCamera} className="btn-primary"><Camera size={13}/> {t.open_camera}</button>
            ) : (
              <>
                <button data-testid="cam-capture" onClick={capture} className="btn-primary"><Camera size={13}/> {t.capture}</button>
                <button data-testid="cam-stop" onClick={stopCamera} className="btn-outline"><X size={12}/> {lang === "ar" ? "إيقاف" : "Stop"}</button>
              </>
            )}
            <label data-testid="cam-gallery" className="btn-outline cursor-pointer">
              <RefreshCw size={12}/> {t.upload_instead}
              <input type="file" accept="image/*" multiple className="hidden" onChange={handleGallery}/>
            </label>
          </div>

          {images.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mt-4">
              {images.map((src, i) => (
                <div key={i} className="relative aspect-square bg-[#070707] border border-white/10 overflow-hidden">
                  <img src={src} alt="" className="w-full h-full object-cover"/>
                  <button onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-red-500/80 text-white p-1"><X size={11}/></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Form */}
        <div className="card-luxe p-5 space-y-3">
          <div className="overline mb-2">2. {lang === "ar" ? "التفاصيل" : "Details"}</div>
          <input data-testid="cam-name" placeholder={t.name} className="input-box" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}/>
          <div className="grid grid-cols-2 gap-3">
            <select className="input-box" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })}>
              {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <div className="flex gap-2">
              <select data-testid="cam-category" className="input-box flex-1" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {cats.map((c) => <option key={c.id} value={c.id}>{lang === "ar" && c.name_ar ? c.name_ar : c.name}</option>)}
              </select>
              <button data-testid="cam-newcat-toggle" type="button" onClick={() => setShowNewCat(!showNewCat)} className="btn-outline text-[10px] whitespace-nowrap" title={t.new_category}><Plus size={11}/></button>
            </div>
          </div>
          {showNewCat && (
            <div className="p-3 border border-copper/30 bg-copper/5 space-y-2">
              <div className="overline">{t.new_cat_label}</div>
              <input data-testid="cam-newcat-name" placeholder={t.new_cat_name} className="input-box" value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })}/>
              <input data-testid="cam-newcat-ar" placeholder={t.new_cat_ar} className="input-box" dir="rtl" value={newCat.name_ar} onChange={(e) => setNewCat({ ...newCat, name_ar: e.target.value })}/>
              <button data-testid="cam-newcat-save" onClick={addCategory} className="btn-primary text-xs"><Tag size={11}/> {t.new_cat_save}</button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="cam-price" type="number" placeholder={t.price} className="input-box" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}/>
            <input type="number" placeholder={t.original_price} className="input-box" value={form.original_price} onChange={(e) => setForm({ ...form, original_price: e.target.value })}/>
            <input type="number" placeholder={t.stock} className="input-box" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })}/>
          </div>
          <textarea rows={3} placeholder={t.description} className="input-box resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}/>

          <div>
            <div className="overline mb-2">3. {t.sections}</div>
            <div className="flex flex-wrap gap-2">
              {SECTIONS.map((sec) => {
                const selected = form.sections.includes(sec.id);
                return (
                  <button
                    key={sec.id}
                    type="button"
                    data-testid={`cam-section-${sec.id}`}
                    onClick={() => toggleSection(sec.id)}
                    className="px-3 py-1.5 text-[10px] uppercase tracking-luxe border transition-colors"
                    style={selected ? { backgroundColor: "#B89766", color: "#fff", borderColor: "#B89766" } : { backgroundColor: "transparent", color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.15)" }}
                  >
                    {lang === "ar" ? sec.ar : sec.en}
                  </button>
                );
              })}
            </div>
            <label className="flex items-center gap-2 text-xs text-white/70 mt-3"><input type="checkbox" checked={form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked })}/> {t.is_new}</label>
          </div>

          <button data-testid="cam-submit" disabled={saving} onClick={submit} className="btn-primary w-full justify-center mt-2">
            <Send size={14}/> {saving ? "…" : t.submit}
          </button>
        </div>
      </div>
    </div>
  );
}
